import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { CardMenu } from "./CardMenu";
import { PhotoViewer } from "./PhotoViewer";
import {
  store, seasonGlance, glanceCopy, fmtDate, fmtShortDate, holesLabel, scorePer18, fairwayPct,
  DEFAULT_CONDITIONS, COMMON_CLUBS, dedupeTrim, todayLocal, estimateHandicap,
  type Round, type BagChange, type RoundSignals,
} from "./store";
import { recapSeason, openWebLogin, fenceCheck, BrainError, FenceError, type BrainErrorCode } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { buildExport } from "./export";
import { BackupMenu } from "./BackupMenu";
import {
  Sparkles, Trophy, Trash2, X, Lock, Flag, WifiOff, RefreshCw, MapPin, TrendingDown,
  Target, Briefcase, Plus, ClipboardList,
} from "lucide-react";
import Chat from "./chat/Chat";

const APP_SCHEME = "oriora-round-card";
const LIST_CAP = 50000;
// SCREENSHOT=0 — sim-only auth bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (the reliable catch for
  // "deleted my account, came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-round-card://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-round-card://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Round Card"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <div style={{ fontSize: "2.4rem", marginBottom: "0.4rem" }}>⛳️</div>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Round Card</div>
        <h1 className="serif" style={{ fontSize: "1.5rem", margin: "0.4rem 0 0.4rem" }}>Every round, hole by hole</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Your scorecard archive lives on
          this device; the season recap runs through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos(APP_SCHEME);
              else if (p === "macos") await signInMac(APP_SCHEME);
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/oriora-round-card/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [rounds, setRounds] = useState<Round[]>([]);
  const [bag, setBag] = useState<BagChange[]>([]);
  const [editing, setEditing] = useState<Round | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [showBag, setShowBag] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Round | null>(null);
  // A round opens as a readable scorecard (read view), not straight into the edit form.
  const [readRound, setReadRound] = useState<Round | null>(null);
  // The full rounds archive + the bag live in a secondary Library reached from home.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not change
  // any existing screen. onChanged → reload() refreshes the archive after a chat-driven mutation.
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const recapRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<(() => void) | null>(null);

  const reload = useCallback(async () => {
    const [r, b] = await Promise.all([store.listRounds({ limit: LIST_CAP }), store.listBagChanges()]);
    setRounds(r); setBag(b);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions (macOS menu bar): Log / Recap / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "log": setEditing(null); setShowLog(true); break;
        case "recap":
          setTimeout(() => {
            recapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            recapRef.current?.querySelector<HTMLButtonElement>("button")?.click();
          }, 50);
          break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const glance = useMemo(() => seasonGlance(rounds), [rounds]);
  // The WHS-style index (PARITY-MATRIX row 3). Null until at least one qualifying round exists —
  // 18 holes carrying the tee's course rating + slope. While it is null the home screen explains
  // it rather than hiding it.
  const hcp = useMemo(() => estimateHandicap(rounds), [rounds]);

  const doExport = useCallback(() => {
    if (rounds.length === 0) return;
    const { csv, filename } = buildExport(rounds);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [rounds]);
  exportRef.current = doExport;

  function startLog(round?: Round) { setEditing(round ?? null); setShowLog(true); }
  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteRound(id);
    await reload();
  }

  return (
    <main className="rc-app">
      {offline && (
        <div role="status" className="row rc-offline">
          <WifiOff size={14} style={{ flexShrink: 0 }} /> Season recap paused — your rounds still save locally
        </div>
      )}

      {/* 🔴 IDENTITY (2026-08-30): there is NO wordmark header row. The LAST ROUND'S SCORE is the
          header — a scoring app opens with the number, not with the app's own name. The backup
          and account controls still have to be reachable, so they collapse into ONE scorecard-cell
          control riding on that headline. Do not reintroduce a wordmark strip, and do not split
          the control back into two circles. */}
      <ScoreHeadline
        round={rounds[0] ?? null}
        menu={
          <CardMenu
            backup={
              <BackupMenu
                appName="Round Card" fileSlug="round-card" appScheme="oriora-round-card"
                hasData={rounds.length > 0} onRestored={reload}
                onExportCsv={rounds.length > 0 ? doExport : undefined}
              />
            }
            account={
              <AccountMenu email={email} appSlug="oriora-round-card" appName="Oriora Round Card" onSignedOut={onSignedOut} />
            }
          />
        }
      />

      {/* TEE BOX — home leads with the log-a-round action; the season totals + index are a quiet
          strip beneath it (demoted from the season-scorecard stat hero). */}
      {/* With rounds logged the tee box leads, as the a3 restructure set it. With ZERO rounds the
          one-line door below IS the log-a-round action, so the big pill would be the same verb
          twice, stacked, pushing the index off the first screen. */}
      {rounds.length > 0 && <TeeBox glance={glance} onLog={() => startLog()} hasRounds />}

      {/* READY-STATE (2026-09-19) — what a golfer with ZERO rounds meets.
          Was: one card reading "Log your first round" with a ⛳️ emoji and a pill, and nothing else.
          The handicap index — the one row the parity matrix rates BETTER, and the number a
          score-keeper actually wants — was invisible until rounds existed (`TeeBox`'s strip is
          gated on hasRounds, and the figures grid lives inside the recap panel). Now the index is
          named and explained on the first screen, and the first round is a one-line door. */}
      {rounds.length === 0 && <FirstRound onSaved={reload} onFullCard={() => startLog()} />}
      {hcp.index === null && <HandicapPanel qualifying={hcp.used} />}

      {rounds.length === 0 ? (
        <AskFlag onAsk={() => setChatOpen(true)} />
      ) : (
        <>
          {/* LEAD WITH THE ROUND — the most recent round's scorecard, opening as a read view (the
              shape every peer's home takes). */}
          <section className="rc-section">
            <div className="rc-section-head">
              <h2 className="rc-section-title">Last round</h2>
              <button className="btn btn-sm btn-pill" onClick={() => setLibraryOpen(true)}><ClipboardList size={13} /> All rounds &amp; bag</button>
            </div>
            <RoundTile r={rounds[0]} onOpen={() => setReadRound(rounds[0])} onDelete={() => setConfirmDelete(rounds[0])} />
            <AskFlag onAsk={() => setChatOpen(true)} />
          </section>

          {/* SEASON RECAP — the differentiator, promoted onto the home surface (not behind a tab). */}
          <div ref={recapRef} className="rc-section">
            <SeasonRecapPanel rounds={rounds} />
          </div>
        </>
      )}

      {readRound && (
        <RoundReadView
          round={readRound}
          onEdit={() => { const r = readRound; setReadRound(null); startLog(r); }}
          onDelete={() => { const r = readRound; setReadRound(null); setConfirmDelete(r); }}
          onClose={() => setReadRound(null)}
        />
      )}

      {libraryOpen && (
        <Library
          rounds={rounds} bag={bag}
          onClose={() => setLibraryOpen(false)}
          onOpenRound={(r) => { setLibraryOpen(false); setReadRound(r); }}
          onDeleteRound={(r) => setConfirmDelete(r)}
          onAddBag={() => setShowBag(true)}
          onDeleteBag={async (id) => { await store.deleteBagChange(id); await reload(); }}
        />
      )}

      {showLog && (
        <LogRound editing={editing} onClose={() => { setShowLog(false); setEditing(null); }} onSaved={() => { setShowLog(false); setEditing(null); void reload(); }} />
      )}
      {showBag && (
        <LogBagChange onClose={() => setShowBag(false)} onSaved={() => { setShowBag(false); void reload(); }} />
      )}

      {confirmDelete && (
        <ModalShell title="Delete round?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem" }}>
            Delete your {fmtDate(confirmDelete.played_on)} round at {confirmDelete.course || "this course"} ({confirmDelete.total_score} over {holesLabel(confirmDelete.holes_played)})? This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
            <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", background: "var(--rose)", borderColor: "var(--rose)" }} onClick={() => doDelete(confirmDelete.id)}>Delete</button>
          </div>
        </ModalShell>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the archive. */}
      {/* 🔴 IDENTITY (2026-08-30): the floating "Chat" pill that used to launch this was DELETED —
          same pill, same corner, same label in ~15 sibling apps. The assistant is NOT removed and
          must never be: its entry is the .rc-flag flagstick on the scorecard edge, which calls
          this same setChatOpen. If a future change moves it, REPLACE it — never drop it. */}
      <aside className={`rcc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="rcc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to log, find, or recap</span>
          <button className="rcc-close" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

// ── TEE BOX — the home's top surface: the log action leads, with the season totals + index as a
//    QUIET strip (this replaces the season-scorecard StatHero; aggregates no longer lead the app,
//    which was the shared-fleet T1 failure). ────────────────────────────────────────────────────
// 🔴 IDENTITY (2026-08-30): the SCORE is the header. Replaces the "⛳️ Round Card" wordmark row.
// A scoring app opens on the number; the app's own name is not news to the person holding it.
// The one collapsed chrome control rides here (see CardMenu) instead of in a wordmark bar.
function ScoreHeadline({ round, menu }: { round: Round | null; menu: React.ReactNode }) {
  const par = round ? (round.holes_played === 9 ? 36 : 72) : 0;
  const diff = round ? round.total_score - par : 0;
  const parLbl = diff === 0 ? "E" : diff > 0 ? `+${diff}` : `${diff}`;
  return (
    <div className="rc-headline">
      <div className="rc-headline__lbl">{round ? "Last round" : "Round Card"}</div>
      <div className="rc-headline__menu">{menu}</div>
      {round ? (
        <>
          <div className="rc-headline__score">{round.total_score}</div>
          <div className="rc-headline__par" style={{ color: diff <= 0 ? "var(--accent)" : "var(--warning)" }}>{parLbl}</div>
          <div className="rc-headline__meta">
            <div className="rc-headline__course">{round.course || "Unnamed course"}</div>
            <div className="rc-headline__when">{fmtDate(round.played_on)} · {round.holes_played === 9 ? "9 holes" : "18 holes"}</div>
          </div>
        </>
      ) : (
        <>
          <div className="rc-headline__score" style={{ color: "var(--ink-3)" }}>—</div>
          <div className="rc-headline__meta">
            <div className="rc-headline__course">No rounds yet</div>
            <div className="rc-headline__when">Log one and your score leads this screen.</div>
          </div>
        </>
      )}
    </div>
  );
}

// 🔴 THE ASSISTANT ENTRY — a flagstick planted on the scorecard's edge, replacing the floating
// "Chat" pill ~15 siblings shared. Rendered on home in BOTH states (rounds logged or none) so the
// AI is always reachable. onAsk opens the same Chat sheet — the AI is moved, never removed.
function AskFlag({ onAsk }: { onAsk: () => void }) {
  return (
    <button type="button" className="rc-flag" onClick={onAsk} aria-label="Open chat assistant">
      <svg className="rc-flag-stick" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 21V3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M7 3.6h11.5L14.6 7.6l3.9 4H7z" fill="currentColor" opacity="0.9" />
        <path d="M4 21h6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      <span className="rc-flag-txt">
        <span className="rc-flag-lbl">Ask</span>
        <span className="rc-flag-sub">rounds · trends · recap</span>
      </span>
    </button>
  );
}

// ── THE INDEX, MET NOT FILED ─────────────────────────────────────────────────────
// PARITY-MATRIX row 3 is the estimated handicap index, and it was the one thing a new golfer
// could not see: the season strip is gated on having rounds, and the "Est. handicap" figure lives
// inside the recap panel, which only renders once rounds exist. So the app's best number was
// invisible for exactly as long as someone was deciding whether to keep the app.
//
// The copy states what the number IS and what makes a round count. It does NOT claim the
// incumbents charge for a handicap — 18Birdies' own page is headed "Free Handicap Calculator"
// (read 2026-09-19), so that claim would be false. What we say instead is true and checkable:
// no subscription, no course database to look the tee up in, and it stays on the device.
function HandicapPanel({ qualifying }: { qualifying: number }) {
  return (
    <section className="rc-hcp" aria-label="Handicap index">
      <div className="rc-hcp__lbl">Handicap index</div>
      <div className="rc-hcp__sub">Builds itself from the rounds you log</div>

      {/* Paper, used the way the identity allows it: a ruled card row INSET in the turf, never a
          general surface. The figure lives ON that line — the place a scorecard puts a number —
          rather than floating beside the heading as a stray em dash. */}
      <div className="rc-hcp__slip">
        <span className="rc-hcp__slip-lbl">Index</span>
        <span className="rc-hcp__slip-val serif tnum">—</span>
        <span className="rc-hcp__slip-note">
          {qualifying === 0 ? "0 qualifying rounds — one starts it" : `${qualifying} qualifying — it sharpens as they build`}
        </span>
      </div>

      <p className="rc-hcp__body">
        A qualifying round is <b>18 holes</b> logged with the tee's <b>course rating</b> and{" "}
        <b>slope</b> — the two numbers printed on the card. Put them in and your index appears;
        it sharpens as rounds build up, from the best differentials of your last 20.
      </p>
      <p className="rc-hcp__foot">
        No subscription and no course database to look the tee up in — the figure is computed here,
        from your own rounds. An estimate for your own use, not an official handicap.
      </p>
    </section>
  );
}

// ── THE DOOR — a first round in one line ─────────────────────────────────────────
// The only way in was `LogRound`, a wide modal with course, date, holes, eighteen per-hole
// steppers, putts, fairways hit/eligible, GIR, rating, slope, conditions, partners and notes.
// That is the right form for a round you care about and the wrong one for the first thirty
// seconds. Course + score + holes saves a real round; rating + slope are optional and are what
// make that round count toward the index, so they sit here rather than behind a disclosure.
function FirstRound({ onSaved, onFullCard }: { onSaved: () => Promise<void> | void; onFullCard: () => void }) {
  const [course, setCourse] = useState("");
  const [score, setScore] = useState("");
  const [holes, setHoles] = useState(18);
  const [rating, setRating] = useState("");
  const [slope, setSlope] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const scoreN = Number(score.trim());
  const ready = course.trim().length > 0 && Number.isFinite(scoreN) && scoreN >= holes;

  async function save() {
    if (!ready || saving) return;
    setSaving(true); setErr(null);
    try {
      const num = (s: string): number | null => {
        const t = s.trim();
        if (!t) return null;
        const n = Number(t);
        return Number.isFinite(n) ? n : null;   // decimal-preserving: a rating is 71.3, not 71
      };
      await store.createRound({
        course: course.trim(),
        played_on: todayLocal(),
        holes_played: holes,
        total_score: Math.round(scoreN),
        course_rating: num(rating),
        slope: num(slope) === null ? null : Math.round(num(slope) as number),
      });
      setCourse(""); setScore(""); setRating(""); setSlope("");
      await onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rc-first" aria-label="Log your first round">
      <div className="rc-first__lbl">Your first round, in one line</div>
      <div className="rc-first__row">
        <input
          className="rc-first__course" placeholder="Course" value={course}
          onChange={(e) => { setCourse(e.target.value); setErr(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
          aria-label="Course"
        />
        <input
          className="rc-first__score tnum" placeholder="Score" inputMode="numeric" value={score}
          onChange={(e) => { setScore(e.target.value.replace(/[^\d]/g, "")); setErr(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
          aria-label="Total score"
        />
      </div>

      <div className="rc-first__row">
        <div className="row" style={{ gap: "0.4rem", flex: 1 }}>
          {[18, 9].map((h) => (
            <button key={h} type="button" className={`chip${holes === h ? " selected" : ""}`}
              style={{ flex: 1, justifyContent: "center", padding: "0.45rem 0" }}
              onClick={() => setHoles(h)}>{h} holes</button>
          ))}
        </div>
      </div>

      <div className="rc-first__row">
        <input className="rc-first__num tnum" placeholder="Rating 71.3" inputMode="decimal" value={rating}
          onChange={(e) => setRating(e.target.value)} aria-label="Course rating (optional)" />
        <input className="rc-first__num tnum" placeholder="Slope 128" inputMode="numeric" value={slope}
          onChange={(e) => setSlope(e.target.value.replace(/[^\d]/g, ""))} aria-label="Slope (optional)" />
      </div>
      <div className="rc-first__hint">
        Rating and slope are optional — they are what make this round count toward your index.
      </div>

      {err && <div className="rc-first__err">{err}</div>}

      <button className="btn btn-primary rc-first__save" disabled={!ready || saving} onClick={save}>
        {saving ? "Saving…" : "Save round"}
      </button>
      <button className="link rc-first__full" onClick={onFullCard}>
        or open the full card — per hole, putts, fairways, partners
      </button>
    </section>
  );
}

function TeeBox({ glance, onLog, hasRounds }: {
  glance: ReturnType<typeof seasonGlance>; onLog: () => void; hasRounds: boolean;
}) {
  return (
    <div className="rc-teebox">
      <button className="btn btn-primary rc-teebox__log" onClick={onLog}><Plus size={18} /> Log round</button>
      {hasRounds && (
        <div className="rc-strip tnum">
          <span><b>{glance.totalRounds}</b> {glance.totalRounds === 1 ? "round" : "rounds"}</span>
          {glance.bestPer18 !== null && <span>best <b>{glance.bestPer18}</b></span>}
          {glance.avgPer18 !== null && <span>avg <b>{glance.avgPer18}</b></span>}
          {glance.index !== null && <span>index <b>{glance.index > 0 ? "+" : ""}{glance.index}</b></span>}
          {glance.lastPlayed && <span className="muted">last {fmtShortDate(glance.lastPlayed)}</span>}
        </div>
      )}
    </div>
  );
}

// ── LIBRARY — the full rounds archive + the bag, a secondary destination reached from home
//    (demoted out of the top-level nav). Each round opens as a scorecard read view; the bag
//    change-log — our gear-evolution differentiator — is kept, here. ───────────────────────────
function Library({ rounds, bag, onClose, onOpenRound, onDeleteRound, onAddBag, onDeleteBag }: {
  rounds: Round[]; bag: BagChange[];
  onClose: () => void;
  onOpenRound: (r: Round) => void; onDeleteRound: (r: Round) => void;
  onAddBag: () => void; onDeleteBag: (id: string) => void;
}) {
  return (
    <ModalShell title="Rounds & bag" onCancel={onClose} wide>
      <div className="rc-section-head">
        <h2 className="rc-section-title">All rounds</h2>
        <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{rounds.length} logged</span>
      </div>
      {rounds.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", padding: "0.25rem 0 1rem" }}>No rounds yet.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
          {rounds.slice(0, 200).map((r) => (
            <RoundTile key={r.id} r={r} onOpen={() => onOpenRound(r)} onDelete={() => onDeleteRound(r)} />
          ))}
        </div>
      )}

      <BagPanel bag={bag} onAdd={onAddBag} onDelete={onDeleteBag} />
    </ModalShell>
  );
}

// Sum of the front / back nine (the OUT / IN of a golf scorecard).
function nineSplit(scores: number[]) {
  return { out: scores.slice(0, 9).reduce((a, b) => a + b, 0), inn: scores.slice(9, 18).reduce((a, b) => a + b, 0) };
}
// One hole cell on the mini scorecard — ≤3 reads as a strong hole, ≥6 as a rough one.
function ScoreCell({ n }: { n: number }) {
  const bg = n <= 3 ? "var(--accent-soft)" : n >= 6 ? "rgba(220,38,38,0.13)" : "rgba(0,0,0,0.05)";
  const fg = n <= 3 ? "var(--accent)" : n >= 6 ? "#b91c1c" : "var(--ink-2)";
  return <span className="tnum" style={{ minWidth: 20, textAlign: "center", fontSize: 12, fontWeight: 600, padding: "3px 0", borderRadius: 5, background: bg, color: fg, flexShrink: 0 }}>{n}</span>;
}
// The OUT / IN nine-total box — fairway green, the scorecard's structural anchor.
function NineBox({ label, v }: { label: string; v: number }) {
  return (
    <span style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minWidth: 30, padding: "0 4px", borderRadius: 5, background: "var(--accent)", color: "#fff", flexShrink: 0 }}>
      <span className="tnum" style={{ fontSize: 12, fontWeight: 800, lineHeight: 1 }}>{v}</span>
      <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.05em", opacity: 0.85 }}>{label}</span>
    </span>
  );
}

// A round renders as a SCORECARD — course + to-par score block, then the per-hole strip with
// OUT / IN nine totals (the golf-log domain signature; cf. TheGrint / Golfshot scorecards).
function RoundTile({ r, onOpen, onDelete }: { r: Round; onOpen: () => void; onDelete: () => void }) {
  const [zoom, setZoom] = useState(false);
  const holes = r.hole_scores ?? [];
  const { out, inn } = nineSplit(holes);
  const par = r.holes_played === 9 ? 36 : 72;
  const diff = r.total_score - par;
  const parLbl = diff === 0 ? "E" : diff > 0 ? `+${diff}` : `${diff}`;
  // Tapping a round opens its scorecard READ view (not the edit form — that's a secondary action
  // inside the read view). This is the leaders' post-round review shape.
  return (
    // 🔴 IDENTITY (2026-08-30): a real ruled SCORECARD lying on the turf — square corners, paper
    // ground (the only light surface in the app), hairline rules between bands, and a shadow that
    // reads as a card resting on grass. Not the rounded tinted .card every sibling also uses.
    <div className="rc-scorecard" onClick={onOpen}>
      {/* ── header: course + date, score + to-par ── */}
      <div className="rc-sc-band rc-sc-head" style={{ display: "flex", alignItems: "center", gap: "0.7rem", padding: "0.75rem 0.9rem 0.6rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: "0.35rem", fontWeight: 700, fontSize: "1rem" }}>
            <MapPin size={13} style={{ color: "var(--paper-ink-2)", flexShrink: 0 }} />
            <span className="rc-sc-course" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.course || "Unnamed course"}</span>
          </div>
          <div className="rc-sc-sub" style={{ fontSize: "var(--fs-xs)", marginTop: 1 }}>{fmtDate(r.played_on)} · {r.holes_played === 9 ? "9 holes" : "18 holes"}</div>
        </div>
        <div style={{ textAlign: "center", minWidth: 50 }}>
          <div className="serif tnum" style={{ fontSize: "1.7rem", fontWeight: 700, lineHeight: 1, color: "var(--paper-ink)" }}>{r.total_score}</div>
          <div className="tnum" style={{ fontSize: "0.72rem", fontWeight: 700, color: diff <= 0 ? "#1d6b3f" : "#8a5210" }}>{parLbl}</div>
        </div>
      </div>
      {/* ── mini scorecard strip ── */}
      {holes.length > 0 && (
        <div className="rc-sc-band" style={{ display: "flex", alignItems: "stretch", gap: 3, padding: "0.5rem 0.9rem 0.55rem", overflowX: "auto" }}>
          {holes.slice(0, 9).map((n, i) => <ScoreCell key={`f${i}`} n={n} />)}
          {holes.length > 9 && <NineBox label="OUT" v={out} />}
          {holes.slice(9, 18).map((n, i) => <ScoreCell key={`b${i}`} n={n} />)}
          {holes.length > 9 && <NineBox label="IN" v={inn} />}
        </div>
      )}
      {/* ── footer: stat chips + photo + delete ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", alignItems: "center", padding: "0 0.9rem 0.7rem" }}>
        {r.fairways_hit !== null && r.fairways_eligible !== null && (
          <span className="rc-sc-chip">FW {fairwayPct(r.fairways_hit, r.fairways_eligible)}</span>
        )}
        {r.greens_in_reg !== null && <span className="rc-sc-chip">GIR {r.greens_in_reg}</span>}
        {r.putts !== null && <span className="rc-sc-chip">{r.putts} putts</span>}
        {r.conditions && <span className="rc-sc-chip">{r.conditions}</span>}
        {/* A round saved through the one-line door carries a total and nothing else, so this band
            was a bare strip of paper with a delete icon on it. Say what the card holds and where
            the rest goes, rather than leaving the scorecard looking half-printed. */}
        {holes.length === 0 && r.putts === null && r.greens_in_reg === null
          && r.fairways_hit === null && !r.conditions && (
          <span className="rc-sc-chip">Total only — open to fill the card</span>
        )}
        {r.photo && (
          <img src={r.photo} alt={`Photo of round at ${r.course || "Unnamed course"}`} title="Tap to view"
            onClick={(ev) => { ev.stopPropagation(); setZoom(true); }}
            style={{ width: 30, height: 30, objectFit: "cover", borderRadius: 6, cursor: "zoom-in", flexShrink: 0, marginLeft: 2 }} />
        )}
        <button className="rc-sc-del" title="Delete round" aria-label="Delete round"
          style={{ marginLeft: "auto", padding: "0.15rem 0.3rem", border: "none", background: "none" }}
          onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
          <Trash2 size={14} />
        </button>
      </div>
      {zoom && r.photo && (
        <PhotoViewer photos={[{ src: r.photo, alt: `Photo of round at ${r.course || "Unnamed course"}` }]} onClose={() => setZoom(false)} />
      )}
    </div>
  );
}

// ── A round's SCORECARD READ VIEW — tapping a round opens this readable hole-by-hole card (the
//    leaders' post-round review), with edit + delete as secondary actions. ──────────────────────
function RoundReadView({ round, onEdit, onDelete, onClose }: {
  round: Round; onEdit: () => void; onDelete: () => void; onClose: () => void;
}) {
  const [zoom, setZoom] = useState(false);
  const holes = round.hole_scores ?? [];
  const { out, inn } = nineSplit(holes);
  const par = round.holes_played === 9 ? 36 : 72;
  const diff = round.total_score - par;
  const parLbl = diff === 0 ? "E" : diff > 0 ? `+${diff}` : `${diff}`;
  const nine = (start: number, count: number, label: string, sum: number) => (
    <div style={{ overflowX: "auto" }}>
      <div className="row" style={{ gap: 3, flexWrap: "nowrap", marginBottom: 4 }}>
        {Array.from({ length: count }, (_, i) => (
          <span key={`h${start + i}`} className="tnum" style={{ minWidth: 20, textAlign: "center", fontSize: 10, color: "var(--ink-3)", fontWeight: 600, flexShrink: 0 }}>{start + i + 1}</span>
        ))}
        <span style={{ minWidth: 30, textAlign: "center", fontSize: 10, color: "var(--ink-3)", fontWeight: 700, flexShrink: 0 }}>{label}</span>
      </div>
      <div className="row" style={{ gap: 3, flexWrap: "nowrap", alignItems: "stretch" }}>
        {holes.slice(start, start + count).map((n, i) => <ScoreCell key={`s${start + i}`} n={n} />)}
        <NineBox label={label} v={sum} />
      </div>
    </div>
  );
  return (
    <ModalShell title={round.course || "Round"} onCancel={onClose} wide>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem", marginBottom: "0.9rem" }}>
        <div style={{ minWidth: 0 }}>
          <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{fmtDate(round.played_on)} · {holesLabel(round.holes_played)}</div>
        </div>
        <div style={{ textAlign: "center", minWidth: 56 }}>
          <div className="serif tnum" style={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1, color: "var(--accent)" }}>{round.total_score}</div>
          <div className="tnum" style={{ fontSize: "0.75rem", fontWeight: 700, color: diff <= 0 ? "var(--accent)" : "#b45309" }}>{parLbl} to par</div>
        </div>
      </div>

      {holes.length > 0 ? (
        <div style={{ display: "grid", gap: "0.9rem", marginBottom: "1rem" }}>
          {nine(0, Math.min(9, holes.length), "OUT", out)}
          {holes.length > 9 && nine(9, holes.length - 9, "IN", inn)}
        </div>
      ) : (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: "1rem" }}>Total-only round — no hole-by-hole scores logged.</div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginBottom: "0.9rem" }}>
        {round.fairways_hit !== null && round.fairways_eligible !== null && <span className="chip" style={{ fontSize: 11 }}>FW {fairwayPct(round.fairways_hit, round.fairways_eligible)}</span>}
        {round.greens_in_reg !== null && <span className="chip" style={{ fontSize: 11 }}>GIR {round.greens_in_reg}</span>}
        {round.putts !== null && <span className="chip" style={{ fontSize: 11 }}>{round.putts} putts</span>}
        {round.conditions && <span className="chip" style={{ fontSize: 11 }}>{round.conditions}</span>}
      </div>

      {round.partners.length > 0 && (
        <div className="row" style={{ gap: "0.3rem", marginBottom: "0.9rem" }}>
          {round.partners.map((p) => <span key={p} className="chip" style={{ fontSize: 11 }}>{p}</span>)}
        </div>
      )}

      {round.notes && <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.55, margin: "0 0 0.9rem", whiteSpace: "pre-wrap" }}>{round.notes}</p>}

      {round.photo && (
        <img src={round.photo} alt={`Photo of round at ${round.course || "course"}`} onClick={() => setZoom(true)}
          style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: "var(--radius)", cursor: "zoom-in", marginBottom: "0.9rem" }} />
      )}

      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" style={{ background: "var(--rose)", borderColor: "var(--rose)", color: "#fff" }} onClick={onDelete}><Trash2 size={14} /> Delete</button>
        <button className="btn btn-primary" onClick={onEdit}>Edit round</button>
      </div>
      {zoom && round.photo && <PhotoViewer photos={[{ src: round.photo, alt: `Photo of round at ${round.course || "course"}` }]} onClose={() => setZoom(false)} />}
    </ModalShell>
  );
}

function BagPanel({ bag, onAdd, onDelete }: { bag: BagChange[]; onAdd: () => void; onDelete: (id: string) => void }) {
  // Reconstruct the current bag locally (replay add/retire) for an at-a-glance view.
  const current = useMemo(() => {
    const set = new Map<string, string>();
    const ordered = [...bag].sort((a, b) => (a.changed_on < b.changed_on ? -1 : a.changed_on > b.changed_on ? 1 : a.created_at.localeCompare(b.created_at)));
    for (const c of ordered) {
      const key = c.club.trim().toLowerCase();
      if (!key) continue;
      if (c.action === "retired") set.delete(key); else set.set(key, c.club.trim());
    }
    return [...set.values()];
  }, [bag]);

  return (
    <div className="card" style={{ padding: "1rem 1.25rem", marginTop: "1.5rem" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.6rem" }}>
        <div className="row" style={{ gap: "0.4rem", fontWeight: 600, fontSize: "var(--fs-sm)" }}>
          <Briefcase size={15} style={{ color: "var(--accent)" }} /> The bag
          <span className="muted" style={{ fontWeight: 400 }}>· {current.length} in play</span>
        </div>
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.3rem 0.55rem", gap: "0.3rem" }} onClick={onAdd}>
          <Plus size={12} /> Log change
        </button>
      </div>

      {current.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginBottom: bag.length ? "0.75rem" : 0 }}>
          {current.map((c) => <span key={c} className="chip" style={{ padding: "0.15rem 0.5rem", fontSize: 11 }}>{c}</span>)}
        </div>
      )}

      {bag.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>
          Log when you add or retire a club. Your bag's evolution over the seasons feeds the recap.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.35rem" }}>
          {bag.slice(0, 8).map((c) => (
            <div key={c.id} className="row" style={{ justifyContent: "space-between", fontSize: "var(--fs-xs)" }}>
              <span className="row" style={{ gap: "0.4rem" }}>
                <span style={{ color: c.action === "added" ? "var(--success)" : "var(--ink-3)", fontWeight: 600 }}>
                  {c.action === "added" ? "+ added" : "– retired"}
                </span>
                <span style={{ fontWeight: 600 }}>{c.club}</span>
                <span className="muted">{fmtShortDate(c.changed_on)}</span>
              </span>
              <button className="btn" aria-label="Delete bag change"
                style={{ padding: "0.15rem 0.3rem", border: "1px solid transparent", background: "none", color: "var(--ink-3)" }}
                onClick={() => onDelete(c.id)}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────── Insights / Season recap ─────────────────────
type RecapErr = { code: BrainErrorCode | "empty" | "fenced"; message: string };

function SeasonRecapPanel({ rounds }: { rounds: Round[] }) {
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<RoundSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  async function run() {
    setBusy(true); setErr(null); setText(null); setSignals(null);
    try {
      // Compute the signals locally first (fast, on-device), then send only those.
      const sig = await store.computeSignals(todayLocal());
      // PRE-LLM fence: defence-in-depth. The recap input is derived signals only, but
      // we screen the corpus' free text (course names / notes) so a coaching-shaped
      // string never slips toward the brain. (The hard refusal of coaching/handicap
      // ASKS is in brain.ts::fenceCheck and is enforced before any network call.)
      const corpusText = rounds.map((r) => `${r.course} ${r.notes}`).join(" ");
      const verdict = fenceCheck(corpusText);
      if (!verdict.ok) throw new FenceError(verdict.message);

      const r = await recapSeason(sig);
      const recap = r.recap.trim();
      if (!recap) {
        setErr({ code: "empty", message: "Not enough logged rounds to recap yet — log a few more." });
      } else {
        setText(recap);
        setSignals(sig);
      }
    } catch (e) {
      if (e instanceof FenceError) setErr({ code: "fenced", message: e.message });
      else if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Recap failed — please try again." });
    } finally { setBusy(false); }
  }

  if (rounds.length === 0) {
    return (
      <div className="card" style={{ padding: "2.5rem 1.5rem", textAlign: "center" }}>
        <TrendingDown size={36} style={{ color: "var(--accent)" }} />
        <div className="serif" style={{ fontSize: "1.2rem", margin: "0.5rem 0 0.25rem" }}>No insights yet</div>
        <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>Log a few rounds and your scoring trend, accuracy, and bag evolution show up here.</div>
      </div>
    );
  }

  const canRecap = rounds.length >= 1;
  return (
    <>
      <DeterministicStats rounds={rounds} />

      <div className="card" style={{ padding: "1rem 1.25rem", marginTop: "1rem", borderLeft: "3px solid var(--accent)" }}>
        <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
          <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem", flexWrap: "wrap" }}>
            <Sparkles size={14} style={{ color: "var(--accent)", flexShrink: 0 }} /> Season recap
          </div>
          <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.7rem", gap: "0.35rem", flexShrink: 0 }} disabled={busy || !canRecap} onClick={run}>
            {busy ? "Reading…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Recap my season"}
          </button>
        </div>

        {!busy && !text && !err && (
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>
            Narrates your logged rounds — scoring trend, fairways/GIR, courses played, bag changes. Describes only; never coaches.
          </div>
        )}

        {busy && (
          <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
            <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
              <Sparkles size={12} style={{ color: "var(--accent)" }} /> Reading across your logged rounds…
            </span>
            <div className="skeleton-line" style={{ width: "100%" }} />
            <div className="skeleton-line" style={{ width: "92%" }} />
            <div className="skeleton-line" style={{ width: "70%" }} />
          </div>
        )}

        {err && !busy && <RecapError err={err} onRetry={run} />}

        {text && !busy && (
          <div style={{ marginTop: "0.6rem" }} aria-live="polite">
            <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: 0 }}>{text}</p>
            <div style={{ marginTop: 8 }}><OrioraBadge /></div>
            {signals && <SignalEvidence signals={signals} />}
            <div className="row" style={{ marginTop: 10 }}>
              <span className="muted" style={{ fontSize: 10 }}>Describes your logged rounds — not coaching, and not an official handicap index.</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function RecapError({ err, onRetry }: { err: RecapErr; onRetry: () => void }) {
  if (err.code === "insufficient_funds") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "rgba(217,147,58,0.1)", border: "1px solid rgba(217,147,58,0.3)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600 }}>Add funds to keep recapping</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then recap again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Hard fence hit — coaching / handicap ask refused on-device, before any AI call.
  if (err.code === "fenced") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Lock size={15} style={{ color: "var(--ink-3)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 10 }}>{err.message}</div>;
  }
  const retryable = err.code === "unavailable" || err.code === "unauthenticated";
  return (
    <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err.message}</span>
      {retryable && err.code === "unavailable" && (
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem", gap: "0.3rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// Deterministic stat cards — computed on-device, ALWAYS shown (no AI, no billing).
// These are the receipts; the recap narrates the same figures.
function DeterministicStats({ rounds }: { rounds: Round[] }) {
  const stats = useMemo(() => {
    const per18 = rounds.map(scorePer18);
    const avg = per18.length ? Math.round((per18.reduce((a, b) => a + b, 0) / per18.length) * 10) / 10 : null;
    const best = per18.length ? Math.min(...per18) : null;

    const fwRounds = rounds.filter((r) => r.fairways_hit !== null && r.fairways_eligible !== null && (r.fairways_eligible ?? 0) > 0);
    const fwHit = fwRounds.reduce((a, r) => a + (r.fairways_hit ?? 0), 0);
    const fwElig = fwRounds.reduce((a, r) => a + (r.fairways_eligible ?? 0), 0);
    const fwPct = fwElig > 0 ? Math.round((fwHit / fwElig) * 100) : null;

    const girRounds = rounds.filter((r) => r.greens_in_reg !== null);
    const girAvg = girRounds.length ? Math.round((girRounds.reduce((a, r) => a + (r.greens_in_reg ?? 0), 0) / girRounds.length) * 10) / 10 : null;

    const courses = new Set(rounds.map((r) => r.course.trim().toLowerCase()).filter(Boolean)).size;
    const hcp = estimateHandicap(rounds);
    return { avg, best, fwPct, girAvg, courses, hcp };
  }, [rounds]);

  const cards: { icon: React.ReactNode; label: string; value: string }[] = [
    { icon: <Target size={15} style={{ color: "var(--accent)" }} />, label: "Est. handicap", value: stats.hcp.index !== null ? String(stats.hcp.index) : "—" },
    { icon: <Trophy size={15} style={{ color: "var(--accent)" }} />, label: "Best 18", value: stats.best !== null ? String(stats.best) : "—" },
    { icon: <TrendingDown size={15} style={{ color: "var(--accent)" }} />, label: "Avg / 18", value: stats.avg !== null ? String(stats.avg) : "—" },
    { icon: <Target size={15} style={{ color: "var(--accent)" }} />, label: "Fairways", value: stats.fwPct !== null ? `${stats.fwPct}%` : "—" },
    { icon: <Flag size={15} style={{ color: "var(--accent)" }} />, label: "GIR / rd", value: stats.girAvg !== null ? String(stats.girAvg) : "—" },
    { icon: <MapPin size={15} style={{ color: "var(--accent)" }} />, label: "Courses", value: String(stats.courses) },
  ];

  return (
    // 🔴 IDENTITY (2026-08-30): the season figures keep their skeleton (a KEEP in the brief) but
    // drop the rounded-card-with-a-gap treatment — square cells sharing the scorecard's ruled
    // language, so the paper scorecard stays the only card-shaped object on the turf.
    <div className="rc-figures">
      {cards.map((c) => (
        <div key={c.label} className="rc-figure" style={{ textAlign: "center" }}>
          <div className="row" style={{ justifyContent: "center", marginBottom: 4 }}>{c.icon}</div>
          <div className="serif tnum" style={{ fontSize: "1.3rem", fontWeight: 600, lineHeight: 1 }}>{c.value}</div>
          <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
}

// The receipts: the actual derived numbers the recap drew from. Purely observational —
// trends, rates, deltas, bag events — so the golfer can see what the AI saw.
function SignalEvidence({ signals }: { signals: RoundSignals }) {
  const chips: { label: string; title: string }[] = [];

  const st = signals.scoring_trend;
  if (st && st.direction !== "flat") {
    const sign = st.strokes_change > 0 ? "+" : "";
    chips.push({
      label: `${st.direction} ${sign}${st.strokes_change.toFixed(1)} strokes`,
      title: `Monthly avg/18 changed ${sign}${st.strokes_change.toFixed(2)} across ${st.months.length} months (slope ${st.slope_per_month.toFixed(2)}/mo). Lower is better.`,
    });
  }

  const fb = signals.form_block;
  if (fb && fb.delta !== null && fb.direction !== "flat" && fb.direction !== "n/a") {
    const sign = fb.delta > 0 ? "+" : "";
    chips.push({
      label: `form ${sign}${fb.delta.toFixed(1)}`,
      title: `Last ${fb.recent_rounds} rounds averaged ${fb.recent_avg_per_18} vs ${fb.prior_avg_per_18} the ${fb.prior_rounds} before`,
    });
  }

  const acc = signals.accuracy;
  if (acc.fairway_rate !== null) {
    const dir = acc.fairway_direction !== "flat" && acc.fairway_direction !== "n/a" ? ` ${acc.fairway_direction}` : "";
    chips.push({
      label: `FW ${Math.round(acc.fairway_rate * 100)}%${dir}`,
      title: `Fairways hit ${Math.round(acc.fairway_rate * 100)}% over ${acc.fairway_rounds_tracked} tracked rounds${acc.fairway_rate_recent !== null && acc.fairway_rate_prior !== null ? ` (recent ${Math.round(acc.fairway_rate_recent * 100)}% vs prior ${Math.round(acc.fairway_rate_prior * 100)}%)` : ""}`,
    });
  }

  if (acc.gir_per_round !== null) {
    chips.push({ label: `GIR ${acc.gir_per_round}`, title: `Greens in regulation per round: ${acc.gir_per_round} over ${acc.gir_rounds_tracked} tracked rounds` });
  }

  const pt = signals.putting;
  if (pt && pt.direction !== "flat" && pt.direction !== "n/a") {
    chips.push({ label: `putts ${pt.direction}`, title: `Putts/18 ${pt.avg_putts_per_18} avg; recent ${pt.recent_avg ?? "—"} vs prior ${pt.prior_avg ?? "—"}` });
  }

  const topCourse = signals.courses[0];
  if (topCourse && topCourse.rounds >= 2) {
    chips.push({ label: `${topCourse.course} ×${topCourse.rounds}`, title: `Most-played: ${topCourse.course}, ${topCourse.rounds} rounds, avg/18 ${topCourse.avg_score_per_18 ?? "—"}` });
  }

  const bag = signals.bag;
  if (bag.added_count > 0 || bag.retired_count > 0) {
    chips.push({ label: `bag +${bag.added_count}/-${bag.retired_count}`, title: `${bag.added_count} club(s) added, ${bag.retired_count} retired this year; ${bag.current_count} now in play` });
  }

  if (chips.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.6rem" }}>
      {chips.map((c) => (
        <span key={c.label} className="chip tnum" title={c.title}
          style={{ padding: "0.1rem 0.45rem", fontSize: 10, background: "var(--accent-soft)", borderColor: "transparent", color: "var(--accent-soft-ink)" }}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

// ─────────────────────────────── Log a round (modal) ─────────────────────────
// Round Card assumes par-72 (4 strokes/hole); it doesn't track per-hole par.
const HOLE_PAR = 4;

// Per-hole score entry — competitor-standard fast scoring (18Birdies / Golfshot style):
// every hole starts at PAR with big +/− steppers, so you only adjust the holes you didn't par.
// Two columns (front / back nine) with live OUT/IN — no typing, fits the screen.
function HoleSteppers({ holes, values, onChange, outSum, inSum }: {
  holes: number; values: number[]; onChange: (i: number, v: number) => void; outSum: number; inSum: number;
}) {
  const STEP: React.CSSProperties = { width: 32, height: 32, flexShrink: 0, borderRadius: 8, border: "1px solid var(--border)", background: "var(--elev-1)", fontSize: 18, lineHeight: 1, color: "var(--ink-2)", cursor: "pointer", display: "grid", placeItems: "center" };
  const color = (n: number) => (n <= HOLE_PAR - 1 ? "var(--accent)" : n >= HOLE_PAR + 2 ? "#b91c1c" : "var(--ink-1)");
  const holeRow = (i: number) => (
    <div key={i} className="row" style={{ gap: 5, alignItems: "center", padding: "2px 0", flexWrap: "nowrap" }}>
      <span style={{ width: 16, textAlign: "right", fontSize: 11, color: "var(--ink-3)", fontWeight: 600, flexShrink: 0 }}>{i + 1}</span>
      <button type="button" aria-label={`Hole ${i + 1} minus`} onClick={() => onChange(i, values[i] - 1)} style={STEP}>−</button>
      <span className="tnum" style={{ width: 20, textAlign: "center", fontSize: 15, fontWeight: 700, color: color(values[i]), flexShrink: 0 }}>{values[i]}</span>
      <button type="button" aria-label={`Hole ${i + 1} plus`} onClick={() => onChange(i, values[i] + 1)} style={STEP}>+</button>
    </div>
  );
  const column = (start: number, end: number, label: string, sum: number) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      {Array.from({ length: end - start }, (_, k) => holeRow(start + k))}
      <div className="row" style={{ justifyContent: "space-between", marginTop: 3, paddingTop: 3, borderTop: "1px solid var(--border-subtle)" }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", color: "var(--ink-3)" }}>{label}</span>
        <span className="tnum" style={{ fontSize: 14, fontWeight: 700, color: "var(--accent)" }}>{sum}</span>
      </div>
    </div>
  );
  return (
    <div className="row" style={{ gap: 12, alignItems: "flex-start", flexWrap: "nowrap" }}>
      {column(0, Math.min(9, holes), "OUT", outSum)}
      {holes > 9 && column(9, holes, "IN", inSum)}
    </div>
  );
}

function LogRound({ editing, onClose, onSaved }: { editing: Round | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [course, setCourse] = useState(editing?.course ?? "");
  const [playedOn, setPlayedOn] = useState(editing?.played_on ?? todayLocal());
  const [holes, setHoles] = useState<number>(editing?.holes_played ?? 18);
  const [total, setTotal] = useState<string>(editing ? String(editing.total_score) : "");
  const [holeScores, setHoleScores] = useState<number[]>(() =>
    Array.from({ length: editing?.holes_played ?? 18 }, (_, i) => editing?.hole_scores?.[i] ?? HOLE_PAR));
  const [holesTouched, setHolesTouched] = useState<boolean>((editing?.hole_scores?.length ?? 0) > 0);
  const [putts, setPutts] = useState<string>(editing?.putts != null ? String(editing.putts) : "");
  const [fwHit, setFwHit] = useState<string>(editing?.fairways_hit != null ? String(editing.fairways_hit) : "");
  const [fwElig, setFwElig] = useState<string>(editing?.fairways_eligible != null ? String(editing.fairways_eligible) : "");
  const [gir, setGir] = useState<string>(editing?.greens_in_reg != null ? String(editing.greens_in_reg) : "");
  const [courseRating, setCourseRating] = useState<string>(editing?.course_rating != null ? String(editing.course_rating) : "");
  const [slope, setSlope] = useState<string>(editing?.slope != null ? String(editing.slope) : "");
  const [conditions, setConditions] = useState(editing?.conditions ?? "");
  const [partners, setPartners] = useState<string[]>(editing ? [...editing.partners] : []);
  const [partnerInput, setPartnerInput] = useState("");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Per-hole steppers default to par; a round saves hole_scores only once the user adjusts one.
  const setHole = (i: number, v: number) => {
    setHolesTouched(true);
    setHoleScores((prev) => prev.map((x, j) => (j === i ? Math.max(1, Math.min(15, v)) : x)));
  };
  const outSum = holeScores.slice(0, 9).reduce((a, n) => a + n, 0);
  const inSum = holeScores.slice(9, holes).reduce((a, n) => a + n, 0);
  const gridSum = outSum + inSum;

  const numOrNull = (s: string): number | null => {
    const t = s.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? Math.round(n) : null;
  };
  // Course rating is the ONE field here that is genuinely fractional — the card says 71.3, and the
  // input's own placeholder says "e.g. 71.3" with inputMode="decimal". Running it through
  // numOrNull silently stored 71, and the rating sits inside the index differential
  // (113/slope × (score − rating)), so every qualifying round was off by up to half a stroke.
  // Putts / fairways / GIR / slope stay integers.
  const decOrNull = (s: string): number | null => {
    const t = s.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  function addPartner() {
    const t = partnerInput.trim();
    if (t && !partners.some((p) => p.toLowerCase() === t.toLowerCase())) setPartners((c) => [...c, t]);
    setPartnerInput("");
  }

  async function save() {
    // Steppers touched → save per-hole scores + auto-summed total; otherwise a total-only round.
    const perHole = holesTouched ? holeScores.slice(0, holes) : [];
    const totalN = perHole.length ? perHole.reduce((a, b) => a + b, 0) : numOrNull(total);
    if (!course.trim()) { setErr("Add the course name."); return; }
    if (totalN === null || totalN < holes) { setErr(`Enter each hole's score, or a total (at least ${holes}).`); return; }
    setSaving(true); setErr(null);
    try {
      const payload = {
        course: course.trim(),
        played_on: playedOn,
        holes_played: holes,
        hole_scores: perHole,
        total_score: totalN,
        putts: numOrNull(putts),
        fairways_hit: numOrNull(fwHit),
        fairways_eligible: numOrNull(fwElig),
        greens_in_reg: numOrNull(gir),
        course_rating: decOrNull(courseRating),
        slope: numOrNull(slope),
        conditions: conditions.trim(),
        partners: dedupeTrim(partners),
        notes: notes.trim(),
      };
      if (isEditing) await store.updateRound(editing!.id, payload);
      else await store.createRound(payload);
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit round" : "Log round"} onCancel={onClose} wide>
      <Field label="Course">
        <input list="course-list" placeholder="e.g. Pebble Beach" value={course} onChange={(e) => setCourse(e.target.value)} />
      </Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-end" }}>
        <Field label="Date" style={{ flex: 1 }}>
          <input type="date" value={playedOn} onChange={(e) => setPlayedOn(e.target.value)} />
        </Field>
        <Field label="Holes" group style={{ flex: 1 }}>
          <div className="row" style={{ gap: "0.4rem" }}>
            {[18, 9].map((h) => (
              <button key={h} type="button" className={`chip${holes === h ? " selected" : ""}`} style={{ flex: 1, justifyContent: "center", padding: "0.45rem 0" }} onClick={() => { setHoles(h); setHoleScores((prev) => Array.from({ length: h }, (_, i) => prev[i] ?? HOLE_PAR)); }}>{h}</button>
            ))}
          </div>
        </Field>
      </div>

      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", margin: "0.2rem 0 0.4rem" }}>
        Hole-by-hole <span className="muted" style={{ fontWeight: 400 }}>(optional — each hole starts at par; tap to adjust)</span>
      </div>
      <HoleSteppers holes={holes} values={holeScores} onChange={setHole} outSum={outSum} inSum={inSum} />

      <Field label="Total score">
        <input inputMode="numeric" placeholder={`Strokes for ${holes} holes`}
          value={holesTouched ? String(gridSum) : total} readOnly={holesTouched}
          onChange={(e) => setTotal(e.target.value)}
          style={holesTouched ? { color: "var(--accent)", fontWeight: 700 } : undefined} />
      </Field>
      {holesTouched && <div className="muted" style={{ fontSize: "var(--fs-xs)", margin: "-2px 0 4px" }}>Auto-summed from your hole-by-hole scores.</div>}

      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", margin: "0.4rem 0 0.5rem" }}>Stats <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></div>
      <div className="row" style={{ gap: "0.6rem" }}>
        <Field label="Putts" style={{ flex: 1 }}><input inputMode="numeric" placeholder="—" value={putts} onChange={(e) => setPutts(e.target.value)} /></Field>
        <Field label="GIR" style={{ flex: 1 }}><input inputMode="numeric" placeholder="—" value={gir} onChange={(e) => setGir(e.target.value)} /></Field>
      </div>
      <div className="row" style={{ gap: "0.6rem" }}>
        <Field label="Fairways hit" style={{ flex: 1 }}><input inputMode="numeric" placeholder="hit" value={fwHit} onChange={(e) => setFwHit(e.target.value)} /></Field>
        <Field label="Fairways possible" style={{ flex: 1 }}><input inputMode="numeric" placeholder="eligible" value={fwElig} onChange={(e) => setFwElig(e.target.value)} /></Field>
      </div>
      <div className="row" style={{ gap: "0.6rem" }}>
        <Field label="Course rating" style={{ flex: 1 }}><input inputMode="decimal" placeholder="e.g. 71.3" value={courseRating} onChange={(e) => setCourseRating(e.target.value)} /></Field>
        <Field label="Slope" style={{ flex: 1 }}><input inputMode="numeric" placeholder="e.g. 128" value={slope} onChange={(e) => setSlope(e.target.value)} /></Field>
      </div>
      <div className="muted" style={{ fontSize: "var(--fs-xs)", margin: "-2px 0 4px" }}>Add rating + slope (from the scorecard, per tee) for the estimated handicap index.</div>

      <Field label="Conditions">
        <input list="conditions-list" placeholder="e.g. windy, links" value={conditions} onChange={(e) => setConditions(e.target.value)} />
      </Field>

      <Field label="Playing partners" group>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: partners.length ? "0.5rem" : 0 }}>
          {partners.map((p) => (
            <button key={p} type="button" className="chip selected" onClick={() => setPartners((c) => c.filter((x) => x !== p))}>{p} <X size={11} /></button>
          ))}
        </div>
        <form className="row" style={{ gap: "0.4rem" }} onSubmit={(e) => { e.preventDefault(); addPartner(); }}>
          <input aria-label="Add a playing partner" placeholder="Add a name…" value={partnerInput} onChange={(e) => setPartnerInput(e.target.value)} style={{ fontSize: "var(--fs-xs)" }} />
          <button type="submit" className="btn" style={{ fontSize: "var(--fs-xs)" }} disabled={!partnerInput.trim()}>Add</button>
        </form>
      </Field>

      <Field label="Notes">
        <textarea rows={2} placeholder="How did the round go?" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>

      <datalist id="course-list" />
      <datalist id="conditions-list">{DEFAULT_CONDITIONS.map((c) => <option key={c} value={c} />)}</datalist>

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", margin: "0.25rem 0 0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.75rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : isEditing ? "Update" : "Save round"}</button>
      </div>
    </ModalShell>
  );
}

// ─────────────────────────────── Log a bag change (modal) ────────────────────
function LogBagChange({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [action, setAction] = useState<"added" | "retired">("added");
  const [club, setClub] = useState("");
  const [changedOn, setChangedOn] = useState(todayLocal());
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!club.trim()) { setErr("Name the club."); return; }
    setSaving(true); setErr(null);
    try {
      await store.createBagChange({ action, club: club.trim(), changed_on: changedOn, notes: notes.trim() });
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Log a bag change" onCancel={onClose}>
      <Field label="Change" group>
        <div className="row" style={{ gap: "0.4rem" }}>
          {(["added", "retired"] as const).map((a) => (
            <button key={a} type="button" className={`chip${action === a ? " selected" : ""}`} style={{ flex: 1, justifyContent: "center", padding: "0.45rem 0" }} onClick={() => setAction(a)}>
              {a === "added" ? "Added" : "Retired"}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Club">
        <input list="club-list" placeholder="e.g. hybrid, 3-iron" value={club} onChange={(e) => setClub(e.target.value)} />
      </Field>
      <Field label="Date">
        <input type="date" value={changedOn} onChange={(e) => setChangedOn(e.target.value)} />
      </Field>
      <Field label="Notes">
        <input placeholder="optional" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <datalist id="club-list">{COMMON_CLUBS.map((c) => <option key={c} value={c} />)}</datalist>

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", margin: "0.25rem 0 0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.75rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save change"}</button>
      </div>
    </ModalShell>
  );
}

// A form field. `group` MUST be set whenever the field holds BUTTONS rather than a text input:
// <button> is a labelable element, so inside a <label> the FIRST button becomes the label's implicit
// control — a tap on the caption text was being forwarded to it (proven 2026-08-28: tapping "Holes"
// silently reset a 9-hole round to 18; tapping "Playing partners" DELETED the first partner chip),
// and that button also inherited the caption as its accessible name. `group` renders a plain
// labelled div instead, which has no implicit control.
function Field({ label, children, style, group }: { label: string; children: React.ReactNode; style?: React.CSSProperties; group?: boolean }) {
  const inner = (
    <>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-xs)", marginBottom: "0.3rem", color: "var(--ink-2)" }}>{label}</div>
      {children}
    </>
  );
  const css: React.CSSProperties = { display: "block", marginBottom: "0.75rem", ...style };
  return group
    ? <div role="group" aria-label={label} style={css}>{inner}</div>
    : <label style={css}>{inner}</label>;
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(31,27,46,0.45)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: wide ? 460 : 340, padding: "1.5rem", boxShadow: "var(--shadow-2)", borderRadius: "var(--radius-dialog)", maxHeight: "90vh", overflowY: "auto", overflowX: "hidden" }}>
        <div className="serif" style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "1rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
